import { useQuery } from "@tanstack/react-query";

import {
  IMasterLanguage,
  IMasterTranslation,
  IMasterTranslationKey,
  ITranslationSyncKeyDetail,
  ITranslationSyncStatus,
  IUsedLocationCount,
} from "../../../@types/admin/AdminMasterTranslations";
import {
  masterTranslationsService,
  translationsSyncService,
} from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { HttpError } from "../../../utils/HttpError";

export const masterTranslationKeysKey = ["admin-master-translation-keys"];

export function masterTranslationsByKeyKey(key: string | undefined) {
  return ["admin-master-translations-by-key", key];
}

export const translationsSyncStatusKey = ["admin-translations-sync-status"];

export function translationsSyncKeyDetailKey(
  key: string | undefined,
  usedLocation: string | undefined
) {
  return ["admin-translations-sync-key-detail", key, usedLocation];
}

/** True when the query failed with the API's "master DB not configured" 503. */
export function isNotConfiguredError(error: unknown): boolean {
  return error instanceof HttpError && error.status === 503;
}

/** Every master key, with its en-GB value and translated languages. */
export function useMasterTranslationKeys() {
  const { data, isLoading, isError, error } = useQuery<IMasterTranslationKey[]>(
    {
      queryKey: masterTranslationKeysKey,
      queryFn: async () => ensureOk(await masterTranslationsService.getKeys()),
      refetchOnMount: "always",
      // A 503 is an environment property, not a transient fault.
      retry: (failureCount, err) =>
        !isNotConfiguredError(err) && failureCount < 2,
    }
  );

  return {
    keys: data ?? [],
    isLoading,
    isError,
    isNotConfigured: isNotConfiguredError(error),
  };
}

/** Every translation row of one key. Idle until a key is selected. */
export function useMasterTranslationsByKey(key: string | undefined) {
  const { data, isLoading, isFetching, isError } = useQuery<
    IMasterTranslation[]
  >({
    queryKey: masterTranslationsByKeyKey(key),
    queryFn: async () =>
      ensureOk(await masterTranslationsService.getByKey(key!)),
    enabled: Boolean(key),
    refetchOnMount: "always",
  });

  return { translations: data ?? [], isLoading, isFetching, isError };
}

/** Language catalogue of the master store. */
export function useMasterLanguages() {
  const { data, isLoading, isError } = useQuery<IMasterLanguage[]>({
    queryKey: ["admin-master-translation-languages"],
    queryFn: async () =>
      ensureOk(await masterTranslationsService.getLanguages()),
  });

  return { languages: data ?? [], isLoading, isError };
}

/** Used locations with counts — feeds the add/rename key selects. */
export function useMasterUsedLocations() {
  const { data, isLoading, isError } = useQuery<IUsedLocationCount[]>({
    queryKey: ["admin-master-translation-used-locations"],
    queryFn: async () =>
      ensureOk(await masterTranslationsService.getUsedLocations()),
  });

  return { usedLocations: data ?? [], isLoading, isError };
}

/** Master vs this environment, per key and language. */
export function useTranslationsSyncStatus() {
  const { data, isLoading, isFetching, isError, error } = useQuery<
    ITranslationSyncStatus[]
  >({
    queryKey: translationsSyncStatusKey,
    queryFn: async () => ensureOk(await translationsSyncService.getStatus()),
    refetchOnMount: "always",
    retry: (failureCount, err) =>
      !isNotConfiguredError(err) && failureCount < 2,
  });

  return {
    status: data ?? [],
    isLoading,
    isFetching,
    isError,
    isNotConfigured: isNotConfiguredError(error),
  };
}

/** Master vs local values of one key. Idle until a key is opened. */
export function useTranslationsSyncKeyDetail(
  key: string | undefined,
  usedLocation: string | undefined
) {
  const { data, isLoading, isError } = useQuery<ITranslationSyncKeyDetail>({
    queryKey: translationsSyncKeyDetailKey(key, usedLocation),
    queryFn: async () =>
      ensureOk(await translationsSyncService.getKeyDetail(key!, usedLocation!)),
    enabled: Boolean(key) && Boolean(usedLocation),
    refetchOnMount: "always",
  });

  return { detail: data, isLoading, isError };
}
